import { useMessageStore } from './messages'
// eslint-disable-next-line quotes
import { defineStore } from "pinia"
import { imageUserDefault, assignEntries } from '@/helpers/'
import { mountLastMessage } from '@/helpers/messages'

import { userInternalChat, usersInternalChat, upUsersInternalChat } from '@/types/internalChatAxios'

interface RootState {
  items: usersInternalChat[];
  user: userInternalChat;
}

export const useUsersStore = defineStore('users', {
  state: (): RootState => ({
    items: [],
    user: {
      id: 0,
      first_name: '',
      last_name: '',
      photo: imageUserDefault()
    }
  }),

  getters: {
    all: (state) => state.items.filter((el) => el.id !== state.user.id)
      .map(item => {
        return {
          id: item.id,
          name: item.first_name + ' ' + item.last_name ?? null,
          photo: item.photo ?? imageUserDefault,
          selected: 'to_user',
          last_message: item.last_message,
          unread_message: item.unread_message,
          typing_messages: false,
          online: item.online,
          has_more_messages: true
        }
      }) ?? [],
    getById: (state) => (id: number) => state.items.find((el) => el.id === id),
    me: (state) => state.user,
    lastMessage () {
      return (userId: number) => {
        const result = this.getById(userId)
        if (result) {
          return result.last_message
        }
        return null
      }
    },
    getMessagesUnread: (state) => {
        return (id: number) => {
            const user = state.items.find(el => el.id === id)
            return user ? user.unread_message : 0
        }
    },
    countUnreadMessage: (state) => state.items.filter(item => item.unread_message > 0).length
  },

  actions: {
    /**
    *  set selected
    */
    upUser (payload: userInternalChat | null) {
      if (!payload) {
        return
      }
      this.user = payload ?? []
    },
    upItems (payload: Array<usersInternalChat> | null) {
      if (!payload) {
        return
      }
      this.items = payload ?? []
    },

    upItem (payload: upUsersInternalChat) {
      if (!payload) return
      const index = this.items.findIndex(p => p.id === payload.id)
      if (payload.id && index !== -1) {
        const result = assignEntries(this.items[index], payload) as usersInternalChat
        this.items[index] = result
      }
    },

    readMessage (userId: number) {
      if (!userId) return
      const index = this.items.findIndex(p => p.id === userId)
      if (index !== -1) {
        this.items[index].unread_message = 0
      }
    },

    upCountUnreadMessage (userId: number, isSelected : boolean = true) {
      if (!userId) return
      const user = this.getById(userId)
      if (!user) return
      const unreadMessages = isSelected ? 0 : (user.unread_message + 1)
      this.upItem({
        id: userId,
        unread_message: unreadMessages
      })
    },
    upTyping (userId: number, status: boolean = false) {
      if (!userId) return
      this.upItem({
        id: userId,
        typing_messages: status
      })
    },
    upStatus (usersIds: number[]) {
      if (!usersIds) return
      const online = this.items.map(item => {
        item.online = usersIds.includes(item.id)
        return item
      })
      this.items = online
    },
    upLastMessage(userId: number) {
      if (!userId) return
      const unread = JSON.parse(JSON.stringify(this.getById(userId)?.unread_message ?? 0))
      const messageStore = useMessageStore()
      const last = messageStore.getLastMessage(userId, 'users')
      if (last) {
        this.upItem({
          id: userId,
          last_message: {
            origin: last.type,
            message: mountLastMessage(last.message),
            date: last.created_at,
            unread,
            type: last.message.type
          }
        })
      }
    }
  }
})
